import { isAdminRole, logger } from "@agent-todo/shared"
import type { Session } from "@supabase/supabase-js"
import { Try } from "functype"
import { type ReactNode, useCallback, useEffect, useMemo, useState } from "react"

import { supabase } from "@/lib/supabase"

import { AuthContext, type AuthContextType, type UserProfile } from "./auth-context"

interface AuthState {
  user: AuthContextType["user"]
  profile: UserProfile | null
  session: Session | null
  loading: boolean
  isAdmin: boolean
}

interface AuthProviderProps {
  children: ReactNode
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [state, setState] = useState<AuthState>({
    user: null,
    profile: null,
    session: null,
    loading: true,
    isAdmin: false,
  })

  const updateAuthState = useCallback(async (session: Session | null) => {
    if (!session?.user) {
      setState({
        user: null,
        profile: null,
        session: null,
        loading: false,
        isAdmin: false,
      })
      return
    }

    const attempt = await Try.fromPromise(
      (async () => {
        return await supabase.from("users").select("id, email, role, created_at").eq("id", session.user.id).single()
      })(),
    )

    attempt.fold(
      (error) => {
        logger.error("Error updating auth state", error as Error, {
          operation: "updateAuthState",
          component: "AuthProvider",
          userId: session.user.id,
        })
        setState({
          user: session.user,
          profile: null,
          session,
          loading: false,
          isAdmin: false,
        })
      },
      ({ data: profile, error }) => {
        if (error) {
          logger.error("Failed to fetch user profile", error, {
            operation: "updateAuthState",
            component: "AuthProvider",
            userId: session.user.id,
          })
          setState({
            user: session.user,
            profile: null,
            session,
            loading: false,
            isAdmin: false,
          })
          return
        }

        const isAdmin = isAdminRole(profile.role)
        setState({
          user: session.user,
          profile,
          session,
          loading: false,
          isAdmin,
        })
      },
    )
  }, [])

  useEffect(() => {
    void supabase.auth.getSession().then(({ data: { session } }) => {
      void updateAuthState(session)
    })

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      void updateAuthState(session)
    })

    return () => subscription.unsubscribe()
  }, [updateAuthState])

  const signIn = useCallback(async (provider: "google" | "github") => {
    const { error } = await supabase.auth.signInWithOAuth({
      provider,
      options: {
        redirectTo: `${window.location.origin}/admin`,
      },
    })

    if (error) {
      logger.error("Sign in error", error, {
        operation: "signIn",
        component: "AuthProvider",
        provider,
      })
      throw error
    }
  }, [])

  const signOut = useCallback(async () => {
    const { error } = await supabase.auth.signOut()
    if (error) {
      logger.error("Sign out error", error, {
        operation: "signOut",
        component: "AuthProvider",
      })
      throw error
    }
  }, [])

  const value = useMemo<AuthContextType>(
    () => ({
      ...state,
      signIn,
      signOut,
    }),
    [state, signIn, signOut],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
